import type { RecapConfig } from "../../lib/api";
import { ALERTS_EVERY_LINE, CADENCES, cadenceLabel, hourLabel } from "./labels";

/**
 * The quiet settings affordance (FR-7.1) — cap, cadence and hour, the three the
 * recipient nudges from the recap; the fuller threshold and routing live in
 * House Rules, which is where the recap points them. A change saves immediately
 * and any filtering-ratio warning (FR-7.2) is stated dryly underneath: a nudge,
 * never a block, so the save has already happened by the time it is read.
 *
 * A pure render of what it is handed — the network state lives in `RecapArea`.
 */

type Props = {
  open: boolean;
  config: RecapConfig | null;
  warning: string | null;
  busy: boolean;
  onCap: (cap: number) => void;
  onCadence: (cadence: string) => void;
  onHour: (hour: number) => void;
};

const FIELD =
  "h-9 rounded-lg border border-border bg-bg/60 px-3 text-sm text-text " +
  "focus-visible:border-border-loud focus-visible:outline-none focus-visible:ring-2 " +
  "focus-visible:ring-focus/40 disabled:opacity-50";

export function RecapConfigPanel({ open, config, warning, busy, onCap, onCadence, onHour }: Props) {
  return (
    <div>
      {/* The toggle lives in the HOST now — the settings plate renders a Row
          ("Morning Nob delivery") so the whole row is the control, instead of
          a small text link the plate's size invited people to miss. */}
      {open && (
        <div className="mt-4 rounded-xl border border-border bg-surface/20 p-5">
          {config === null ? (
            <p className="nob-breathe text-sm text-faint">Reading your settings.</p>
          ) : (
            <>
              <p className="text-[13px] leading-relaxed text-faint">
                When the Morning Nob arrives, and how many it holds. The rest — what qualifies, when
                to stay quiet — is written in your House Rules.
              </p>

              <div className="mt-5 flex flex-wrap items-end gap-6">
                <label className="flex flex-col gap-1.5">
                  <span className="text-[12px] uppercase tracking-[0.12em] text-faint">Cap</span>
                  <input
                    type="number"
                    min={1}
                    max={50}
                    inputMode="numeric"
                    value={config.cap}
                    disabled={busy}
                    onChange={(e) => {
                      const next = Number.parseInt(e.target.value, 10);
                      if (Number.isFinite(next)) onCap(next);
                    }}
                    className={`${FIELD} w-20 tabular-nums`}
                    aria-label="Most items per delivery"
                  />
                </label>

                <div className="flex flex-col gap-1.5">
                  <span className="text-[12px] uppercase tracking-[0.12em] text-faint">
                    Cadence
                  </span>
                  <div className="flex items-center gap-1.5">
                    {CADENCES.map((cadence) => {
                      const active = config.cadence === cadence;
                      return (
                        <button
                          key={cadence}
                          type="button"
                          onClick={() => onCadence(cadence)}
                          disabled={busy}
                          aria-pressed={active}
                          className={`h-9 rounded-lg border px-3.5 text-sm transition-colors duration-75
                                     disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2
                                     focus-visible:ring-focus/70 focus-visible:ring-offset-2
                                     focus-visible:ring-offset-bg ${
                                       active
                                         ? "border-accent/50 bg-accent/10 text-accent-ink-strong"
                                         : "border-border text-text-soft hover:border-border-loud hover:text-text"
                                     }`}
                        >
                          {cadenceLabel(cadence)}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <label className="flex flex-col gap-1.5">
                  <span className="text-[12px] uppercase tracking-[0.12em] text-faint">Hour</span>
                  <select
                    value={config.hour}
                    disabled={busy}
                    onChange={(e) => {
                      const next = Number.parseInt(e.target.value, 10);
                      if (Number.isFinite(next)) onHour(next);
                    }}
                    className={FIELD}
                    aria-label="Delivery hour, recipient-local"
                  >
                    {Array.from({ length: 24 }, (_, h) => (
                      <option key={h} value={h}>
                        {hourLabel(h)}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              {/* REALTIME (ruling R-0910-3): there is no digest to time, so the
                  plate says what the door actually does instead of leaving an
                  hour and a cadence standing over a digest nobody sends. */}
              {config.alert_mode === "every" && (
                <p className="mt-5 text-[13px] leading-relaxed text-faint">{ALERTS_EVERY_LINE}</p>
              )}

              {/* The filtering-ratio nudge (FR-7.2) — stated, never enforced. */}
              {warning && (
                <p className="mt-5 border-l-2 border-accent/40 pl-3 text-[13px] leading-relaxed text-accent-ink-strong/80">
                  {warning}
                </p>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
